/**
 * DmChatArea
 * Main chat area for DM conversations.
 * Displays messages, handles send/edit/delete, and listens for real-time updates.
 */

import { useQueryClient } from "@tanstack/react-query"
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Calendar,
  MoreVertical,
  Trash2,
  UserRound,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useRealtimeEvent } from "@/contexts/RealtimeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import {
  useDeleteDmConversation,
  useDeleteDmMessage,
  useDmConversation,
  useEditDmMessage,
  useMarkDmAsRead,
  useSendDmMessage,
} from "../../hooks/useDmChat"
import {
  useArchiveConversation,
  useConnectionContext,
  useUnarchiveConversation,
} from "../../hooks/useUnifiedConversations"
import type { DmMessage } from "../../types/dm-chat.types"
import { bookingCounterpartyName } from "../../utils/bookingCounterparty"
import { formatBookingSlot } from "../../utils/formatBookingSlot"
import { getMessagesWithSenderHeader } from "../../utils/messageGrouping"
import { MessageInput } from "../shared/MessageInput"
import { DmMessageBubble } from "./DmMessageBubble"

function DmChatAreaInner({ id }: { id: string }) {
  const { t } = useTranslation("chats")
  const navigate = useNavigate()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const markAsReadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState("")
  const [editingMessage, setEditingMessage] = useState<DmMessage | null>(null)

  const user = useAuthStore(s => s.user)
  const queryClient = useQueryClient()
  const { data, isLoading } = useDmConversation(id)
  const sendMessage = useSendDmMessage()
  const editMessage = useEditDmMessage()
  const deleteMessage = useDeleteDmMessage()
  const markAsRead = useMarkDmAsRead()
  const archiveConversation = useArchiveConversation()
  const unarchiveConversation = useUnarchiveConversation()
  const deleteConversation = useDeleteDmConversation()

  const otherUser = data?.conversation?.otherUser
  const ritual = data?.conversation?.ritual ?? null
  const bookingContext = data?.conversation?.bookingContext ?? null
  const isArchived = data?.conversation?.isArchived ?? false
  const messages = data?.messages ?? []
  // Connection context (community/ritual relationship banner) is only meaningful
  // for plain DMs. Suppress it for ritual-scoped DMs since we already render a
  // booking banner there.
  const { data: connectionContext } = useConnectionContext(!ritual ? otherUser?.id : undefined)

  const debouncedMarkAsRead = useCallback(
    (chatId: string) => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current)
      }
      markAsReadTimeoutRef.current = setTimeout(() => {
        markAsRead.mutate(chatId)
      }, 300)
    },
    [markAsRead],
  )

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (messages.length > 0) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "auto",
          block: "end",
        })
      })
    }
  }, [messages.length])

  // Mark as read on open
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when conversation id changes
  useEffect(() => {
    if (id) {
      markAsRead.mutate(id)
    }
  }, [id])

  // Auto-scroll to bottom only when new messages arrive and user is near bottom
  const prevMessageCountRef = useRef(messages.length)
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const container = scrollContainerRef.current
      if (container) {
        const isNearBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight < 150
        if (isNearBottom) {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
        }
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
      }
    }
    prevMessageCountRef.current = messages.length
  }, [messages.length])

  // WebSocket: new message.
  // The global notifications listener (useGlobalChatNotifications in AppLayout)
  // already invalidates the conversation lists and ["dm-conversation", <id>] for
  // every dm:new_message. To avoid duplicate refetches (issue #528), here we
  // only mark the OPEN conversation as read.
  useRealtimeEvent(
    "dm:new_message",
    useCallback(
      (payload: unknown) => {
        const msg = payload as DmMessage
        if (msg.conversationId === id) {
          debouncedMarkAsRead(id)
        }
      },
      [id, debouncedMarkAsRead],
    ),
  )

  // WebSocket: message edited
  useRealtimeEvent(
    "dm:message_edited",
    useCallback(
      (payload: unknown) => {
        const msg = payload as { conversationId: string }
        if (msg.conversationId === id) {
          queryClient.invalidateQueries({
            queryKey: ["dm-conversation", id],
          })
        }
      },
      [id, queryClient],
    ),
  )

  // WebSocket: message deleted
  useRealtimeEvent(
    "dm:message_deleted",
    useCallback(
      (payload: unknown) => {
        const msg = payload as { conversationId: string }
        if (msg.conversationId === id) {
          queryClient.invalidateQueries({
            queryKey: ["dm-conversation", id],
          })
        }
        queryClient.invalidateQueries({
          queryKey: ["dm-conversations"],
        })
        queryClient.invalidateQueries({
          queryKey: ["all-conversations"],
        })
      },
      [id, queryClient],
    ),
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim()) return

    if (editingMessage) {
      editMessage.mutate(
        {
          conversationId: id,
          messageId: editingMessage.id,
          content: inputValue.trim(),
        },
        {
          onSuccess: () => {
            setEditingMessage(null)
            setInputValue("")
          },
          onError: () => {
            toast.error("Failed to edit message")
          },
        },
      )
    } else {
      sendMessage.mutate(
        { conversationId: id, content: inputValue.trim() },
        {
          onSuccess: () => {
            setInputValue("")
          },
          onError: () => {
            toast.error("Failed to send message")
          },
        },
      )
    }
  }

  const handleEdit = (message: DmMessage) => {
    setEditingMessage(message)
    setInputValue(message.content)
  }

  const handleCancelEdit = () => {
    setEditingMessage(null)
    setInputValue("")
  }

  const handleDelete = (messageId: string) => {
    deleteMessage.mutate(
      { conversationId: id, messageId },
      {
        onError: () => {
          toast.error("Failed to delete message")
        },
      },
    )
  }

  if (isLoading || !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="size-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header — two layouts:
          - Ritual-scoped DM (e.g. expert chat): ritual cover + title on row 1,
            "Coaching Session" badge + expert avatar/name on row 2
          - Plain DM: single row with the other user's avatar + name */}
      <div className="h-[64px] px-4 md:px-[30px] py-[17px] flex items-center border-b border-border min-h-14">
        <Button variant="ghost" size="icon-xs" asChild className="md:hidden shrink-0">
          <Link to="/chats">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <div className="flex-1 flex items-center justify-center md:justify-start min-w-0">
          {ritual ? (
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={`/rituals/${ritual.id}`}
                className="size-10 rounded-md shrink-0 overflow-hidden no-underline"
                aria-label={`Open ritual ${ritual.title}`}
              >
                {ritual.profilePhoto ? (
                  <img src={ritual.profilePhoto} alt="" className="size-full object-cover" />
                ) : (
                  <div className="size-full bg-[var(--color-chat-border)]" />
                )}
              </Link>
              <div className="flex flex-col gap-0.5 min-w-0">
                <Link
                  to={`/rituals/${ritual.id}`}
                  className="text-sm font-semibold text-foreground truncate no-underline hover:opacity-80"
                >
                  {ritual.title}
                </Link>
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="size-3.5" />
                    Coaching Session
                  </span>
                  {otherUser?.id && (
                    <Link
                      to={`/profile/${otherUser.id}`}
                      className="inline-flex items-center gap-1.5 no-underline hover:opacity-80"
                    >
                      <Avatar
                        src={otherUser.profilePhoto || undefined}
                        alt={otherUser.name || "Expert"}
                        fallback={otherUser.name || "?"}
                        size="sm"
                        className="size-4"
                      />
                      <span className="text-foreground">{otherUser.name}</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ) : otherUser?.id ? (
            <Link
              to={`/profile/${otherUser.id}`}
              className="flex items-center !gap-2.5 no-underline hover:opacity-80 transition-opacity"
            >
              <Avatar
                src={otherUser.profilePhoto || undefined}
                alt={otherUser.name || "User"}
                fallback={otherUser.name || "?"}
                size="md"
              />
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-primary">
                  {otherUser.name || "Unknown user"}
                </span>
                <span className="text-sm font-semibold text-primary flex md:hidden">
                  @{otherUser.username}
                </span>
              </div>
            </Link>
          ) : (
            <>
              <Avatar
                src={otherUser?.profilePhoto || undefined}
                alt={otherUser?.name || "User"}
                fallback={otherUser?.name || "?"}
                size="md"
              />
              <span className="text-sm font-semibold text-foreground">
                {otherUser?.name || "Unknown user"}
              </span>
            </>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="More options"
              className="text-foreground hover:opacity-70 transition-opacity"
              title="More options"
            >
              <MoreVertical className="size-6" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {otherUser?.id && (
              <DropdownMenuItem onClick={() => navigate(`/profile/${otherUser.id}`)}>
                <UserRound />
                View profile
              </DropdownMenuItem>
            )}
            {otherUser?.id && <DropdownMenuSeparator />}
            {isArchived ? (
              <DropdownMenuItem
                disabled={unarchiveConversation.isPending}
                onClick={() =>
                  unarchiveConversation.mutate(id, {
                    onSuccess: () => {
                      toast.success("Conversation unarchived")
                    },
                    onError: () => toast.error("Failed to unarchive conversation"),
                  })
                }
              >
                <ArchiveRestore />
                Unarchive
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={archiveConversation.isPending}
                onClick={() =>
                  archiveConversation.mutate(id, {
                    onSuccess: () => {
                      toast.success("Conversation archived")
                      navigate("/chats")
                    },
                    onError: () => toast.error("Failed to archive conversation"),
                  })
                }
              >
                <Archive />
                Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                deleteConversation.mutate(id, {
                  onSuccess: () => {
                    toast.success("Conversation deleted")
                    navigate("/chats")
                  },
                  onError: () => toast.error("Failed to delete conversation"),
                })
              }
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-5 md:px-[30px] w-full py-4 space-y-4"
      >
        {bookingContext && (
          <p className="text-[13px] text-muted-foreground text-center py-4 max-w-[600px] mx-auto leading-relaxed">
            Your session with{" "}
            <span className="font-semibold text-foreground">
              {bookingCounterpartyName({
                currentUserId: user?.id,
                ...bookingContext,
              })}
            </span>{" "}
            has been booked for{" "}
            <span className="font-semibold text-foreground">
              {formatBookingSlot(bookingContext)}
            </span>
            . Use this chat to share details, ask questions, or prepare for the session together.
          </p>
        )}

        {messages.length === 0 && !connectionContext && !bookingContext && (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">
              Send a message to start the conversation
            </p>
          </div>
        )}
        <div className="max-w-[925px] mx-auto flex flex-col gap-4">
          {(() => {
            const headerMessageIds = getMessagesWithSenderHeader(messages)
            return messages.map(message => (
              <DmMessageBubble
                key={message.id}
                message={message}
                isOwn={message.senderId === user?.id}
                senderId={otherUser?.id}
                senderName={otherUser?.name}
                senderPhoto={otherUser?.profilePhoto}
                showSenderInfo={headerMessageIds.has(message.id)}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))
          })()}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput
        input={inputValue}
        onInputChange={setInputValue}
        onSubmit={handleSubmit}
        isLoading={sendMessage.isPending || editMessage.isPending}
        editMode={!!editingMessage}
        onCancelEdit={handleCancelEdit}
        editLabel={t("dmMessage.editing")}
        placeholder={t("dmMessage.placeholder")}
        autoFocus
      />
    </div>
  )
}

export function DmChatArea() {
  const { conversationId } = useParams<{ conversationId: string }>()
  if (!conversationId) return null
  return <DmChatAreaInner key={conversationId} id={conversationId} />
}
